/**
 * Checklist templates aligned with MD/Compliance/Compliance.md & MD/TODO.md §1.4
 * Stages 1–4 — CRM pipeline (anchor = enquiry date or derived from admission).
 * Stage 6 — Admission in progress; 7A–7E — active resident.
 * dueDaysFromAnchor: days from workflow anchor (0 = anchor day).
 */

export const WORKFLOW_KEYS = {
  /** CRM pipeline — pre-resident (anchor = enquiry date or derived from admission) */
  STAGE1_ENQUIRY: 'stage1_enquiry',
  STAGE2_QUALIFIED: 'stage2_qualified',
  STAGE3_TOUR: 'stage3_tour',
  STAGE4_WAITLIST: 'stage4_waitlist',
  STAGE5_OFFER: 'stage5_offer',
  STAGE6_ADMISSION: 'stage6_admission',
  STAGE7A_SETTLING: 'stage7a_settling',
  STAGE7B_STEADY: 'stage7b_steady_state',
  STAGE7C_ESCALATED: 'stage7c_escalated',
  STAGE7D_RESPITE: 'stage7d_respite',
  STAGE7E_PALLIATIVE: 'stage7e_palliative',
  STAGE8_EXIT: 'stage8_exit',
};

/**
 * @typedef {Object} ChecklistTemplateItem
 * @property {string} templateId
 * @property {string} title
 * @property {string} [description]
 * @property {import('../workflow/taskModel').TaskCategory} category
 * @property {string} ownerRole
 * @property {number} dueDaysFromAnchor
 * @property {'low'|'normal'|'high'|'critical'} priority
 * @property {string[]} [dependsOnTemplateIds] — optional ordering
 */

/** Stage 1 — Enquiry (anchor = enquiry logged date). Aligns with TODO.md §1.4 Stage 1. */
export const STAGE1_ENQUIRY_TEMPLATE = [
  {
    templateId: 's1_log',
    title: 'Enquiry logged in CRM',
    description: 'Source, contact details, and prospective resident identity captured.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 0,
    priority: 'high',
  },
  {
    templateId: 's1_ack',
    title: 'Acknowledgement sent to contact',
    description: 'Email/SMS acknowledgement within policy window (e.g. 2 hours).',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 0,
    priority: 'high',
  },
  {
    templateId: 's1_callback',
    title: 'Follow-up call scheduled (24h)',
    description: 'Callback or appointment booked within 24 hours of enquiry.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 1,
    priority: 'high',
  },
  {
    templateId: 's1_owner',
    title: 'Admissions coordinator assigned',
    description: 'Owner set for pipeline tracking and handover.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 0,
    priority: 'normal',
  },
  {
    templateId: 's1_mac',
    title: 'My Aged Care referral verified (if applicable)',
    description: 'Referral code or pathway documented.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 2,
    priority: 'normal',
  },
  {
    templateId: 's1_acat',
    title: 'If ACAT incomplete: advise family to contact My Aged Care',
    description: 'Task and document advice when no ACAT / approval yet.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 2,
    priority: 'normal',
  },
  {
    templateId: 's1_emergency',
    title: 'If emergency / urgent: same-day contact attempted',
    description: 'Escalation when hospital or emergency pathway flagged.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 0,
    priority: 'critical',
  },
  {
    templateId: 's1_duplicate',
    title: 'Duplicate lead / existing client check',
    description: 'Avoid duplicate entries; link to existing enquiry if match.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 1,
    priority: 'normal',
  },
];

/** Stage 2 — Qualified lead */
export const STAGE2_QUALIFIED_TEMPLATE = [
  {
    templateId: 's2_recorded',
    title: 'Qualification decision recorded',
    description: 'Lead qualified or disqualified with reason.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 0,
    priority: 'high',
  },
  {
    templateId: 's2_acat',
    title: 'ACAT approval type and date verified',
    description: 'Permanent / respite / transition; approval date on file.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 1,
    priority: 'high',
  },
  {
    templateId: 's2_means',
    title: 'Means testing status and pathway',
    description: 'Lodged, completed, or awaiting; funding pathway noted.',
    category: 'finance',
    ownerRole: 'Finance',
    dueDaysFromAnchor: 3,
    priority: 'normal',
  },
  {
    templateId: 's2_score',
    title: 'Lead score and temperature (Hot / Warm / Cool / Cold)',
    description: 'Scoring model applied per facility policy.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 2,
    priority: 'normal',
  },
  {
    templateId: 's2_clinical',
    title: 'Clinical suitability snapshot',
    description: 'Mobility, dementia, behaviours — wing/room matching inputs.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 3,
    priority: 'high',
  },
  {
    templateId: 's2_lost',
    title: 'Disqualification or lost (if applicable)',
    description: 'Reason captured; moved to lost pipeline when relevant.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 5,
    priority: 'normal',
  },
];

/** Stage 3 — Tour & assessment */
export const STAGE3_TOUR_TEMPLATE = [
  {
    templateId: 's3_schedule',
    title: 'Tour or visit scheduled',
    description: 'Date/time with family; calendar invite where used.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 0,
    priority: 'high',
  },
  {
    templateId: 's3_pretour',
    title: 'Pre-tour information sent',
    description: 'FAQ, fees overview, what to bring.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 1,
    priority: 'normal',
  },
  {
    templateId: 's3_tour_done',
    title: 'Tour conducted and notes captured',
    description: 'Attendees, impressions, objections, next steps.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 7,
    priority: 'high',
  },
  {
    templateId: 's3_preadm',
    title: 'Pre-admission clinical screen completed',
    description: 'Screening or Assessment #34 as per policy.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 7,
    priority: 'high',
    dependsOnTemplateIds: ['s3_tour_done'],
  },
  {
    templateId: 's3_outcome',
    title: 'Tour outcome recorded',
    description: 'Positive / negative / deferred / follow-up required.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 8,
    priority: 'high',
    dependsOnTemplateIds: ['s3_tour_done'],
  },
  {
    templateId: 's3_followup',
    title: 'Post-tour follow-up communication sent',
    description: 'Email or call summary to family.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 9,
    priority: 'normal',
  },
];

/** Stage 4 — Waitlist */
export const STAGE4_WAITLIST_TEMPLATE = [
  {
    templateId: 's4_entry',
    title: 'Waitlist entry confirmed',
    description: 'Intent to proceed; CRM record in waitlist state.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 0,
    priority: 'high',
  },
  {
    templateId: 's4_priority',
    title: 'Priority band set',
    description: 'Standard / Priority / Emergency per policy.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 0,
    priority: 'high',
  },
  {
    templateId: 's4_position',
    title: 'Queue position and matching rules',
    description: 'Auto or manual position; room type / wing matching.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 1,
    priority: 'normal',
  },
  {
    templateId: 's4_contact',
    title: 'Contact frequency plan',
    description: 'Weekly / fortnightly etc. per urgency.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 1,
    priority: 'normal',
  },
  {
    templateId: 's4_bed',
    title: 'Bed offer matching criteria updated',
    description: 'Preferences and clinical needs for allocation.',
    category: 'admission',
    ownerRole: 'Facility Manager',
    dueDaysFromAnchor: 3,
    priority: 'normal',
  },
  {
    templateId: 's4_review',
    title: 'Next review / contact date scheduled',
    description: 'Ongoing nurture until offer or withdrawal.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 14,
    priority: 'normal',
  },
];

/** Stage 5 — Offer / Agreement (anchor = quote generation date). */
export const STAGE5_OFFER_TEMPLATE = [
  {
    templateId: 's5_quote',
    title: 'Generate quote / funding outcome calculation',
    description: 'RAD/DAP options, means-tested fee estimate, supplements.',
    category: 'finance',
    ownerRole: 'Finance',
    dueDaysFromAnchor: 0,
    priority: 'high',
  },
  {
    templateId: 's5_doc_pack',
    title: 'Agreement document pack generation',
    description: 'Resident agreement, accommodation supplement, extra-service docs.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 1,
    priority: 'high',
    dependsOnTemplateIds: ['s5_quote'],
  },
  {
    templateId: 's5_send_esign',
    title: 'Send agreement for e-signature',
    description: 'Dispatch via e-signature platform to family / representative.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 1,
    priority: 'high',
    dependsOnTemplateIds: ['s5_doc_pack'],
  },
  {
    templateId: 's5_track_esign',
    title: 'Track e-signature completion',
    description: 'Monitor signing status; send reminders as needed.',
    category: 'admission',
    ownerRole: 'System',
    dueDaysFromAnchor: 3,
    priority: 'high',
    dependsOnTemplateIds: ['s5_send_esign'],
  },
  {
    templateId: 's5_offer_expiry',
    title: 'Offer expiry monitoring — 7-day default',
    description: 'Auto-expire or escalate if unsigned by deadline.',
    category: 'admission',
    ownerRole: 'System',
    dueDaysFromAnchor: 7,
    priority: 'critical',
    dependsOnTemplateIds: ['s5_send_esign'],
  },
  {
    templateId: 's5_store_signed',
    title: 'Store signed agreement PDF',
    description: 'Archive executed agreement in document management system.',
    category: 'admission',
    ownerRole: 'System',
    dueDaysFromAnchor: 0,
    priority: 'high',
    dependsOnTemplateIds: ['s5_track_esign'],
  },
  {
    templateId: 's5_convert',
    title: 'CRM → Resident profile one-click conversion',
    description: 'Convert CRM lead to active resident record; carry forward all data.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 0,
    priority: 'critical',
    dependsOnTemplateIds: ['s5_store_signed'],
  },
  {
    templateId: 's5_auto_stage6',
    title: 'Auto-instantiate Stage 6 admission checklist',
    description: 'System creates Stage 6 tasks upon successful conversion.',
    category: 'admission',
    ownerRole: 'System',
    dueDaysFromAnchor: 0,
    priority: 'high',
    dependsOnTemplateIds: ['s5_convert'],
  },
  {
    templateId: 's5_confirm_date',
    title: 'Confirm admission date with family',
    description: 'Phone + written confirmation of target move-in date.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 1,
    priority: 'high',
    dependsOnTemplateIds: ['s5_convert'],
  },
  {
    templateId: 's5_notify_clinical',
    title: 'Notify clinical team of incoming admission',
    description: 'Alert RN / clinical coordinator with expected date and care summary.',
    category: 'clinical',
    ownerRole: 'System',
    dueDaysFromAnchor: 0,
    priority: 'normal',
    dependsOnTemplateIds: ['s5_convert'],
  },
];

/** @type {ChecklistTemplateItem[]} */
export const STAGE6_ADMISSION_TEMPLATE = [
  {
    templateId: 's6_confirm_date',
    title: 'Confirm admission date with family',
    description: 'Phone + written confirmation (Compliance §Stage 6).',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: -3,
    priority: 'high',
  },
  {
    templateId: 's6_profile',
    title: 'Create / verify resident profile in system',
    description: 'CRM → profile conversion; demographics complete.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 0,
    priority: 'critical',
  },
  {
    templateId: 's6_room_nfc',
    title: 'Assign room and configure NFC / QR tag',
    description: 'IT / Maintenance — link room to resident profile.',
    category: 'admission',
    ownerRole: 'IT',
    dueDaysFromAnchor: 0,
    priority: 'high',
  },
  {
    templateId: 's6_pre_adm_pack',
    title: 'Clinical pre-admission pack received',
    description: 'Hospital discharge summary, medication list, transfer summary.',
    category: 'clinical',
    ownerRole: 'Clinical',
    dueDaysFromAnchor: 0,
    priority: 'high',
  },
  {
    templateId: 's6_gp',
    title: 'GP details confirmed',
    description: 'Contact GP; confirm will continue prescribing.',
    category: 'clinical',
    ownerRole: 'Clinical',
    dueDaysFromAnchor: 1,
    priority: 'normal',
  },
  {
    templateId: 's6_pharmacy',
    title: 'Pharmacy assigned',
    description: 'Link internal or external pharmacy.',
    category: 'clinical',
    ownerRole: 'Clinical',
    dueDaysFromAnchor: 0,
    priority: 'normal',
  },
  {
    templateId: 's6_mar',
    title: 'Medication chart (MAR) prepared',
    description: 'RN — Day of admission from discharge meds or GP list.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 0,
    priority: 'critical',
  },
  {
    templateId: 's6_initial_assessment',
    title: 'Initial clinical assessments scheduled / started',
    description: 'Falls, PI, nutrition, cognitive, pain — within 24h.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 1,
    priority: 'high',
  },
  {
    templateId: 's6_care_plan',
    title: 'Care plan created (target within 48h of assessments)',
    description: 'Auto-generated where supported; RN sign-off.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 3,
    priority: 'high',
    dependsOnTemplateIds: ['s6_initial_assessment'],
  },
  {
    templateId: 's6_acer',
    title: 'ACER lodgement tracked (28-day regulatory deadline)',
    description: 'Finance/Admin — Services Australia AC021; monitor T-14/T-7/T-3.',
    category: 'finance',
    ownerRole: 'Finance',
    dueDaysFromAnchor: 28,
    priority: 'critical',
  },
  {
    templateId: 's6_means',
    title: 'Means assessment follow-up',
    description: 'Combined Assets and Income if not lodged pre-admission.',
    category: 'finance',
    ownerRole: 'Finance',
    dueDaysFromAnchor: 14,
    priority: 'normal',
  },
  {
    templateId: 's6_portal',
    title: 'Family portal invitations sent',
    description: 'Admin — Day of admission.',
    category: 'admission',
    ownerRole: 'Admin',
    dueDaysFromAnchor: 0,
    priority: 'normal',
  },
  {
    templateId: 's6_welcome',
    title: 'Welcome pack provided',
    description: 'Statement of Rights, facility guide, contacts.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 0,
    priority: 'normal',
  },
  {
    templateId: 's6_dietary',
    title: 'Dietary requirements confirmed with kitchen',
    description: 'IDDSI level, allergies, preferences.',
    category: 'clinical',
    ownerRole: 'Kitchen',
    dueDaysFromAnchor: 0,
    priority: 'high',
  },
  {
    templateId: 's6_weight',
    title: 'Initial weight recorded (NQIP baseline)',
    description: 'RN — Day of admission.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 0,
    priority: 'normal',
  },
];

/** @type {ChecklistTemplateItem[]} */
export const STAGE7A_SETTLING_TEMPLATE = [
  {
    templateId: 's7a_orientation',
    title: 'Resident orientation completed',
    description: 'Tour, meal times, call bell — Day 1–2.',
    category: 'settling',
    ownerRole: 'Care staff',
    dueDaysFromAnchor: 2,
    priority: 'high',
  },
  {
    templateId: 's7a_assessments',
    title: 'Initial assessments completed (Day 1–14)',
    description: 'All mandatory domains per Resident.md.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 14,
    priority: 'critical',
  },
  {
    templateId: 's7a_care_plan_signed',
    title: 'Care plan signed off',
    description: 'Within 48h of assessments complete.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 16,
    priority: 'high',
    dependsOnTemplateIds: ['s7a_assessments'],
  },
  {
    templateId: 's7a_acer_watch',
    title: 'ACER countdown — on track for day 28',
    description: 'Escalate if at risk; hard regulatory deadline.',
    category: 'finance',
    ownerRole: 'Finance',
    dueDaysFromAnchor: 21,
    priority: 'critical',
  },
  {
    templateId: 's7a_portal_activation',
    title: 'Family portal activation verified',
    description: 'Track invites sent Day 1; follow up non-activation.',
    category: 'admission',
    ownerRole: 'Admin',
    dueDaysFromAnchor: 7,
    priority: 'normal',
  },
  {
    templateId: 's7a_day14',
    title: 'Day 14 settling-in satisfaction check',
    description: 'Admissions coordinator — call or visit family.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 14,
    priority: 'normal',
  },
  {
    templateId: 's7a_day28',
    title: 'Day 28 settling-in review meeting',
    description: 'RN + care staff; transition readiness to 7B steady state.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 28,
    priority: 'high',
  },
];

/** @type {ChecklistTemplateItem[]} */
export const STAGE7B_STEADY_TEMPLATE = [
  {
    templateId: 's7b_care_plan_review',
    title: '8-weekly care plan review',
    description: 'RN-led review with resident/family; update goals, interventions, preferences.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 56,
    priority: 'high',
  },
  {
    templateId: 's7b_assessment_reassessment',
    title: 'Scheduled assessment reassessments',
    description: 'Falls quarterly, nutrition quarterly, pressure injury as needed, cognitive 6-monthly.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 90,
    priority: 'high',
  },
  {
    templateId: 's7b_anacc_review',
    title: 'AN-ACC classification review',
    description: 'Finance/Clinical — check if reassessment indicated by care-need changes.',
    category: 'finance',
    ownerRole: 'Finance',
    dueDaysFromAnchor: 90,
    priority: 'normal',
  },
  {
    templateId: 's7b_medication_review',
    title: 'Medication review (RMMR / QUM)',
    description: 'Pharmacist-led; reconcile chart, check interactions, de-prescribing opportunities.',
    category: 'clinical',
    ownerRole: 'Clinical',
    dueDaysFromAnchor: 90,
    priority: 'normal',
  },
  {
    templateId: 's7b_monthly_statement',
    title: 'Monthly care statement generated',
    description: 'Auto-generated from clinical data; distributed to family portal.',
    category: 'admission',
    ownerRole: 'Admin',
    dueDaysFromAnchor: 30,
    priority: 'normal',
  },
  {
    templateId: 's7b_family_meeting',
    title: 'Family meeting / relationship check-in',
    description: 'Scheduled or as-needed; document outcomes and action items.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 90,
    priority: 'normal',
  },
  {
    templateId: 's7b_quality_compliance',
    title: 'Quality & compliance tracking (per resident)',
    description: 'Restraint reviews, NQIP indicator alignment, infection surveillance, wound tracking.',
    category: 'quality',
    ownerRole: 'RN',
    dueDaysFromAnchor: 90,
    priority: 'normal',
  },
  {
    templateId: 's7b_weight_monitoring',
    title: 'Weight monitoring (monthly)',
    description: 'NQIP — track unplanned weight loss >5% in 30 days; escalate if triggered.',
    category: 'clinical',
    ownerRole: 'Care staff',
    dueDaysFromAnchor: 30,
    priority: 'normal',
  },
];

/** @type {ChecklistTemplateItem[]} */
export const STAGE7C_ESCALATED_TEMPLATE = [
  {
    templateId: 's7c_escalation_triggered',
    title: 'Escalation event documented',
    description: 'Record trigger: recurrent falls, new PI stage 2+, weight loss >5%, behaviour, SIRS P1.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 0,
    priority: 'critical',
  },
  {
    templateId: 's7c_family_notification',
    title: 'Family / representative notified',
    description: 'Within 24h of escalation; document contact attempt and response.',
    category: 'admission',
    ownerRole: 'RN',
    dueDaysFromAnchor: 1,
    priority: 'high',
    dependsOnTemplateIds: ['s7c_escalation_triggered'],
  },
  {
    templateId: 's7c_gp_notification',
    title: 'GP / specialist notified',
    description: 'Clinical referral or urgent review request.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 1,
    priority: 'high',
    dependsOnTemplateIds: ['s7c_escalation_triggered'],
  },
  {
    templateId: 's7c_enhanced_monitoring',
    title: 'Enhanced monitoring plan activated',
    description: 'Increased observation frequency; daily progress notes minimum; weight/vitals increased.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 1,
    priority: 'critical',
    dependsOnTemplateIds: ['s7c_escalation_triggered'],
  },
  {
    templateId: 's7c_care_plan_update',
    title: 'Care plan updated for escalated needs',
    description: 'Immediate interim care plan; adjusted interventions and goals.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 2,
    priority: 'high',
    dependsOnTemplateIds: ['s7c_escalation_triggered'],
  },
  {
    templateId: 's7c_mdt_review',
    title: 'Multidisciplinary team review',
    description: 'RN, GP, allied health (physio, OT, speech, dietitian), pharmacist — within 7 days.',
    category: 'clinical',
    ownerRole: 'Clinical',
    dueDaysFromAnchor: 7,
    priority: 'high',
    dependsOnTemplateIds: ['s7c_care_plan_update'],
  },
  {
    templateId: 's7c_anacc_reassessment',
    title: 'AN-ACC reassessment considered',
    description: 'If care needs increased → request reassessment for higher classification.',
    category: 'finance',
    ownerRole: 'Finance',
    dueDaysFromAnchor: 14,
    priority: 'normal',
    dependsOnTemplateIds: ['s7c_mdt_review'],
  },
  {
    templateId: 's7c_resolution',
    title: 'Escalation resolved or transitioned',
    description: 'Return to 7B (steady state) or transition to 7E (palliative).',
    category: 'clinical',
    ownerRole: 'Clinical',
    dueDaysFromAnchor: 28,
    priority: 'high',
  },
];

/** @type {ChecklistTemplateItem[]} */
export const STAGE7D_RESPITE_TEMPLATE = [
  {
    templateId: 's7d_respite_admission',
    title: 'Respite admission documented',
    description: 'Approved respite days recorded; start/end dates confirmed.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 0,
    priority: 'high',
  },
  {
    templateId: 's7d_initial_assessments',
    title: 'Respite initial assessments (abbreviated)',
    description: 'Falls, medication reconciliation, dietary, key clinical screens.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 1,
    priority: 'high',
  },
  {
    templateId: 's7d_care_plan',
    title: 'Respite care plan created',
    description: 'Abbreviated care plan based on referral + initial assessment.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 2,
    priority: 'high',
    dependsOnTemplateIds: ['s7d_initial_assessments'],
  },
  {
    templateId: 's7d_days_tracking',
    title: 'Approved respite days tracked',
    description: 'Monitor remaining approved days; alert when approaching limit.',
    category: 'finance',
    ownerRole: 'Finance',
    dueDaysFromAnchor: 7,
    priority: 'normal',
  },
  {
    templateId: 's7d_conversion_check',
    title: 'Respite-to-permanent conversation (if applicable)',
    description: 'If approaching end of approved period → discuss permanent admission with family.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 14,
    priority: 'normal',
  },
  {
    templateId: 's7d_discharge_planning',
    title: 'Discharge planning (if returning home)',
    description: 'Coordinate with community services, GP, family; discharge summary prepared.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 21,
    priority: 'normal',
  },
];

/** @type {ChecklistTemplateItem[]} */
export const STAGE7E_PALLIATIVE_TEMPLATE = [
  {
    templateId: 's7e_eol_identified',
    title: 'End-of-life / palliative phase identified',
    description: 'Clinical assessment, GP recommendation, or specialist input documented.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 0,
    priority: 'critical',
  },
  {
    templateId: 's7e_advance_care_directive',
    title: 'Advance care directive reviewed / confirmed',
    description: 'Check existing ACD or facilitate new conversation; document decisions.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 1,
    priority: 'critical',
    dependsOnTemplateIds: ['s7e_eol_identified'],
  },
  {
    templateId: 's7e_family_meeting',
    title: 'Family meeting — goals of care discussion',
    description: 'Document family wishes, comfort measures, spiritual/cultural needs.',
    category: 'admission',
    ownerRole: 'RN',
    dueDaysFromAnchor: 2,
    priority: 'critical',
    dependsOnTemplateIds: ['s7e_eol_identified'],
  },
  {
    templateId: 's7e_palliative_referral',
    title: 'Palliative care team referral',
    description: 'Specialist palliative care consultation if not already engaged.',
    category: 'clinical',
    ownerRole: 'Clinical',
    dueDaysFromAnchor: 2,
    priority: 'high',
  },
  {
    templateId: 's7e_comfort_care_plan',
    title: 'Comfort care plan activated',
    description: 'Symptom management, PRN medications, environmental comfort, dignity measures.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 1,
    priority: 'critical',
    dependsOnTemplateIds: ['s7e_advance_care_directive'],
  },
  {
    templateId: 's7e_medication_review',
    title: 'Medication review for comfort focus',
    description: 'Cease non-essential medications; ensure adequate PRN analgesia/anxiolytics.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 1,
    priority: 'high',
  },
  {
    templateId: 's7e_spiritual_cultural',
    title: 'Spiritual / cultural support arranged',
    description: 'Pastoral care, cultural liaison, or chaplaincy as per resident/family preferences.',
    category: 'settling',
    ownerRole: 'Care staff',
    dueDaysFromAnchor: 2,
    priority: 'normal',
  },
  {
    templateId: 's7e_bereavement_prep',
    title: 'Bereavement support plan for family',
    description: 'Resources, counselling referrals, post-death administrative guidance.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 3,
    priority: 'normal',
  },
];

/** Stage 8 — Exit / Discharge (anchor = discharge or death date). */
export const STAGE8_EXIT_TEMPLATE = [
  {
    templateId: 's8_status',
    title: 'Update resident status to discharged/deceased',
    description: 'Set official status and record date/time in system.',
    category: 'admission',
    ownerRole: 'Admin',
    dueDaysFromAnchor: 0,
    priority: 'critical',
  },
  {
    templateId: 's8_notify_sa',
    title: 'Notify Services Australia within 14 days',
    description: 'Departure notification via AC021; regulatory deadline.',
    category: 'finance',
    ownerRole: 'Finance',
    dueDaysFromAnchor: 14,
    priority: 'high',
    dependsOnTemplateIds: ['s8_status'],
  },
  {
    templateId: 's8_rad_refund',
    title: 'RAD refund calculation',
    description: 'Calculate and process refundable accommodation deposit within legislated timeframe.',
    category: 'finance',
    ownerRole: 'Finance',
    dueDaysFromAnchor: 14,
    priority: 'high',
    dependsOnTemplateIds: ['s8_status'],
  },
  {
    templateId: 's8_final_billing',
    title: 'Exit billing / final statement',
    description: 'Generate final invoice; reconcile outstanding fees and charges.',
    category: 'finance',
    ownerRole: 'Finance',
    dueDaysFromAnchor: 14,
    priority: 'high',
    dependsOnTemplateIds: ['s8_status'],
  },
  {
    templateId: 's8_room_cleared',
    title: 'Room cleared and cleaned',
    description: 'Personal belongings collected; deep clean and inspection completed.',
    category: 'maintenance',
    ownerRole: 'Housekeeping',
    dueDaysFromAnchor: 2,
    priority: 'normal',
    dependsOnTemplateIds: ['s8_status'],
  },
  {
    templateId: 's8_nfc_deassign',
    title: 'NFC/QR tag de-assigned',
    description: 'Remove resident association from room tag; reset for next allocation.',
    category: 'maintenance',
    ownerRole: 'IT',
    dueDaysFromAnchor: 0,
    priority: 'normal',
    dependsOnTemplateIds: ['s8_status'],
  },
  {
    templateId: 's8_records_archived',
    title: 'Clinical records archived',
    description: 'Move active clinical records to archive per retention policy.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 0,
    priority: 'high',
    dependsOnTemplateIds: ['s8_status'],
  },
  {
    templateId: 's8_discharge_summary',
    title: 'Discharge summary generated',
    description: 'Clinical discharge summary prepared for GP and receiving facility.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 0,
    priority: 'high',
    dependsOnTemplateIds: ['s8_status'],
  },
  {
    templateId: 's8_mhr_update',
    title: 'My Health Record updated',
    description: 'Upload discharge summary to My Health Record where applicable.',
    category: 'clinical',
    ownerRole: 'RN',
    dueDaysFromAnchor: 0,
    priority: 'high',
    dependsOnTemplateIds: ['s8_status'],
  },
  {
    templateId: 's8_portal_deactivation',
    title: 'Family portal access deactivation scheduled',
    description: 'Schedule portal deactivation after grace period; notify family.',
    category: 'admission',
    ownerRole: 'Admin',
    dueDaysFromAnchor: 14,
    priority: 'normal',
  },
  {
    templateId: 's8_condolence',
    title: 'Condolence communication if deceased',
    description: 'Facility Manager sends condolence letter/card to family.',
    category: 'admission',
    ownerRole: 'Facility Manager',
    dueDaysFromAnchor: 2,
    priority: 'high',
  },
  {
    templateId: 's8_bed_available',
    title: 'Bed availability updated in system',
    description: 'Mark room/bed as available for new admission or maintenance.',
    category: 'admission',
    ownerRole: 'System',
    dueDaysFromAnchor: 0,
    priority: 'high',
    dependsOnTemplateIds: ['s8_status'],
  },
  {
    templateId: 's8_bereavement_14',
    title: 'Bereavement follow-up — 14 days',
    description: 'Check-in call or letter to family; offer support resources.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 14,
    priority: 'normal',
  },
  {
    templateId: 's8_bereavement_90',
    title: 'Bereavement follow-up — 90 days',
    description: 'Final follow-up with family; close bereavement support file.',
    category: 'admission',
    ownerRole: 'Admissions',
    dueDaysFromAnchor: 90,
    priority: 'low',
  },
];

const TEMPLATE_MAP = {
  [WORKFLOW_KEYS.STAGE1_ENQUIRY]: STAGE1_ENQUIRY_TEMPLATE,
  [WORKFLOW_KEYS.STAGE2_QUALIFIED]: STAGE2_QUALIFIED_TEMPLATE,
  [WORKFLOW_KEYS.STAGE3_TOUR]: STAGE3_TOUR_TEMPLATE,
  [WORKFLOW_KEYS.STAGE4_WAITLIST]: STAGE4_WAITLIST_TEMPLATE,
  [WORKFLOW_KEYS.STAGE5_OFFER]: STAGE5_OFFER_TEMPLATE,
  [WORKFLOW_KEYS.STAGE6_ADMISSION]: STAGE6_ADMISSION_TEMPLATE,
  [WORKFLOW_KEYS.STAGE7A_SETTLING]: STAGE7A_SETTLING_TEMPLATE,
  [WORKFLOW_KEYS.STAGE7B_STEADY]: STAGE7B_STEADY_TEMPLATE,
  [WORKFLOW_KEYS.STAGE7C_ESCALATED]: STAGE7C_ESCALATED_TEMPLATE,
  [WORKFLOW_KEYS.STAGE7D_RESPITE]: STAGE7D_RESPITE_TEMPLATE,
  [WORKFLOW_KEYS.STAGE7E_PALLIATIVE]: STAGE7E_PALLIATIVE_TEMPLATE,
  [WORKFLOW_KEYS.STAGE8_EXIT]: STAGE8_EXIT_TEMPLATE,
};

export function getTemplateByWorkflowKey(workflowKey) {
  return TEMPLATE_MAP[workflowKey] || [];
}
